import { ApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  type DetailsProps,
  EmptyStation,
  type FormActionData,
  formatDateTime,
  getFormDiff,
  InfoPanel,
  MessageBar,
  type Nullable,
  type ObjectSchemaDefinition,
  Paragraph,
  ReadOnlyField,
  Section,
  SelectField,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, type FormikValues, useFormikContext } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ensureError } from '../../../../common/errors/ensure-error';
import {
  type IdpUser,
  IdpUserDocument,
  type IdpUserQuery,
  type IdpUserQueryVariables,
  type UpdateIdpUserMutationVariables,
  useDeleteIdpUserMutation,
  UserStatus,
  UserStoreCategory,
  useUpdateIdpUserMutation,
} from '../../../../generated/axauth-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';

type idpUser = Nullable<UpdateIdpUserMutationVariables['input']['patch']>;

const idpUserSchema = Yup.object<ObjectSchemaDefinition<idpUser>>({
  firstName: Yup.string().label('First Name').max(200),
  lastName: Yup.string().label('Last Name').max(200),
  status: Yup.string().label('Status'),
});

export const UserDetail: React.FC = () => {
  const { envId, userStoreId, userId } = useParams<{
    envId: string;
    userStoreId: string;
    userId: string;
  }>();

  const [data, setData] = useState<IdpUserQuery>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const envApolloClient = useRef<ApolloClient<Record<string, unknown>>>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of axauth-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );
        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          envApolloClient.current = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );
          const idpUserResult = await envApolloClient.current.query<
            IdpUserQuery,
            IdpUserQueryVariables
          >({
            query: IdpUserDocument,
            variables: {
              id: userId,
            },
            fetchPolicy: 'no-cache',
          });

          setData(idpUserResult.data);
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, envId]);

  const [updateIdpUser] = useUpdateIdpUserMutation({
    client: envApolloClient.current,
    fetchPolicy: 'no-cache',
  });

  const { actions } = useActions(
    envId,
    userStoreId,
    userId,
    data?.idpUser?.idpUserStore?.category,
    envApolloClient.current,
  );

  const onSubmit = useCallback(
    async (
      formData: FormikValues,
      initialData: DetailsProps<idpUser>['initialData'],
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      await updateIdpUser({
        variables: {
          input: {
            id: userId,
            patch: getFormDiff(formData, initialData.data),
          },
        },
      });
    },
    [userId, updateIdpUser],
  );

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="User Details">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<idpUser>
          defaultTitle={
            data?.idpUser
              ? `${data.idpUser.firstName} ${data.idpUser.lastName}`
              : ''
          }
          subtitle="User Details"
          actions={actions}
          validationSchema={idpUserSchema}
          initialData={{
            data: data?.idpUser || {},
            loading,
            error: error,
          }}
          saveData={onSubmit}
          infoPanel={<Panel />}
        >
          <Form />
        </Details>
      )}
    </>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<IdpUser>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="User Account ID">{values.id}</Paragraph>
        <Paragraph title="Created">
          {formatDateTime(values.createdAt)} by {values.createdBy}
        </Paragraph>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
        <Paragraph title="User Store Name">
          {values.idpUserStore?.name}
        </Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC = () => {
  return (
    <>
      <Field name="firstName" label="First Name" as={SingleLineTextField} />
      <Field name="lastName" label="Last Name" as={SingleLineTextField} />
      <Field name="email" label="Email Address" as={ReadOnlyField} />
      <Field
        name="emailVerified"
        label="Email Verified"
        options={[
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ]}
        as={SelectField}
      />
      <Field
        name="status"
        label="Status"
        options={[
          { value: UserStatus.Active, label: 'Active' },
          { value: UserStatus.Blocked, label: 'Blocked' },
        ]}
        as={SelectField}
      />
    </>
  );
};

const getDeleteConfirmationBody = (stationName: string): string =>
  `Deleting the user account from the user store will stop the email/password ` +
  `authentication flow, but the same email still may be used by other Identity ` +
  `Providers for authenticating. If you wish to block this email from being used ` +
  `entirely regardless of the Identity Provider, you can block the account from ` +
  `the Management System's ${stationName}.`;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useActions(
  envId: string,
  userStoreId: string,
  userId: string,
  storeCategory?: UserStoreCategory | null,
  envApolloClient?: ApolloClient<Record<string, unknown>>,
) {
  const history = useHistory();

  const [deleteIdpUser] = useDeleteIdpUserMutation({
    client: envApolloClient,
    fetchPolicy: 'no-cache',
  });

  const deleteConfirmationBody =
    storeCategory === UserStoreCategory.EndUser
      ? getDeleteConfirmationBody('Application User Details')
      : getDeleteConfirmationBody('User Details');

  const actions: FormActionData[] = [
    {
      label: 'Reset Password',
      path: `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/provisionedUsers/${userId}/resetPassword`,
    },
    {
      label: 'Delete',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: deleteConfirmationBody,
      },
      onActionSelected: async () => {
        await deleteIdpUser({
          variables: {
            input: {
              id: userId,
            },
          },
          fetchPolicy: 'no-cache',
        });

        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/provisionedUsers`,
        );
      },
    },
  ];

  return { actions } as const;
}

export const UserDetailCrumb: BreadcrumbResolver = ({ envId, userId }) => {
  const envApolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
  );

  return async (): Promise<string> => {
    const response = await envApolloClient.query<IdpUserQuery>({
      query: IdpUserDocument,
      variables: { id: userId },
      errorPolicy: 'ignore',
    });
    const fullName = `${response.data.idpUser?.firstName} ${response.data.idpUser?.lastName}`;
    return fullName || 'User Details';
  };
};
